/* eslint-disable @typescript-eslint/no-explicit-any */
import axios from 'axios'
import type { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios'
import type {
  RequestConfig,
  RequestInterceptors,
  CancelRequestSource
} from './types'
import { ElMessage, ElLoading } from 'element-plus'

import oauth from '@/utils/oauth'

const originalAxios = axios.create({
  timeout: 5000
})

originalAxios.interceptors.request.use(
  (config) => {
    // set token here
    // config.headers.token = ''
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

originalAxios.interceptors.response.use(
  (res) => {
    if (res.status >= 200 && res.status < 300) {
      return res!.data
    }
  },
  (error) => {
    // alert(error)
    console.log(error)
    return new Promise(() => {})
  }
)

let reqNum = 0
const showLoading = () => {
  if (reqNum === 0) {
    ElLoading.service({
      lock: true,
      fullscreen: true,
      background: 'rgba(255, 255, 255, 0.7)',
      customClass: 'request-loading'
    })
  }
  reqNum += 1
}

const closeLoading = () => {
  if (reqNum <= 0) return
  reqNum -= 1
  if (reqNum === 0) {
    ElLoading.service().close()
  }
}

class Request {
  instance: AxiosInstance
  interceptorsObj?: RequestInterceptors
  cancelRequestSourceList: CancelRequestSource[]
  requestUrlList?: string[]

  constructor(config: RequestConfig) {
    this.instance = axios.create(config)
    this.interceptorsObj = config.interceptors
    this.requestUrlList = []
    this.cancelRequestSourceList = []

    this.instance.interceptors.request.use(
      (config: AxiosRequestConfig) => {
        const { tokenType, token } = oauth.getTokens()
        // @ts-ignore
        if (!config?.noLoading) {
          showLoading()
        }
        if (!config.data) {
          config.data = true
        }
        config.headers = {
          ...config.headers,
          Authorization: `${tokenType} ${token}`,
          // Authorization: 'MCT-COMMON-CI-TOKEN',
          userName: localStorage.getItem('USER_CEC') || '',
          'Content-Type': 'application/json; charset=UTF-8'
        }
        // @ts-ignore call iss api with username will cause CORS issue
        if (config.noUserName) {
          delete config.headers.userName
        }

        return config
      },
      (err: unknown) => Promise.reject(err)
    )

    this.instance.interceptors.request.use(
      this.interceptorsObj?.requestInterceptors,
      this.interceptorsObj?.requestInterceptorsCatch
    )
    this.instance.interceptors.response.use(
      this.interceptorsObj?.responseInterceptors,
      this.interceptorsObj?.responseInterceptorsCatch
    )

    this.instance.interceptors.response.use(
      (res: AxiosResponse) => {
        closeLoading()
        if (res.status >= 200 && res.status < 300) {
          // @ts-ignore
          if (res.config?.withOutErrorCode) {
            return res!.data
          }
          if (res.data?.errorCode === 'OKOKOK') {
            return res!.data
          } else {
            ElMessage({
              type: 'error',
              message: res.data?.errorMessage
            })
            return new Promise(() => {})
          }
        }
      },
      (err: any) => {
        closeLoading()
        const errMessage =
          err.response?.data?.errorMessage ||
          err?.message ||
          'Something went wrong. Please try again later. If the problem persists, please contact support.'
        console.log('Error: ', err)
        ElMessage({
          type: 'error',
          message: errMessage
        })
        return new Promise(() => {})
      }
    )
  }

  private getSourceIndex(url: string): number {
    return this.cancelRequestSourceList?.findIndex(
      (item: CancelRequestSource) => {
        return Object.keys(item)[0] === url
      }
    ) as number
  }

  private deleteUrl(url: string) {
    const urlIndex = this.requestUrlList?.findIndex((item) => item === url)
    const sourceIndex = this.getSourceIndex(url)
    urlIndex !== -1 && this.requestUrlList?.splice(urlIndex as number, 1)
    sourceIndex !== -1 && this.cancelRequestSourceList?.splice(sourceIndex, 1)
  }

  request<T>(config: RequestConfig): Promise<T> {
    return new Promise((resolve, reject) => {
      if (config.interceptors?.requestInterceptors) {
        config = config.interceptors?.requestInterceptors(config)
      }
      const { url } = config
      if (url) {
        this.requestUrlList?.push(url)
        config.cancelToken = new axios.CancelToken((cancelToken) => {
          this.cancelRequestSourceList?.push({
            [url]: cancelToken
          })
        })
      }

      this.instance
        .request<any, T>(config)
        .then((res) => {
          if (config.interceptors?.responseInterceptors) {
            res = config.interceptors.responseInterceptors<T>(res)
          }
          resolve(res)
        })
        .catch((err: any) => reject(err))
        .finally(() => {
          url && this.deleteUrl(url)
        })
    })
  }

  cancelRequest(url: string | string[]) {
    if (typeof url === 'string') {
      // cancel one request
      const sourceIndex = this.getSourceIndex(url)
      sourceIndex >= 0 && this.cancelRequestSourceList?.[sourceIndex][url]()
    } else {
      // cancel multiple requests
      url.forEach((url) => {
        const sourceIndex = this.getSourceIndex(url)
        sourceIndex >= 0 && this.cancelRequestSourceList?.[sourceIndex][url]()
      })
    }
  }

  cancelAllRequest() {
    this.cancelRequestSourceList?.forEach((source) => {
      // eslint-disable-next-line prefer-destructuring
      const key = Object.keys(source)[0]
      source[key]()
    })
  }
}

// @ts-ignore
const ENV = window.envConfig
const apiBaseURL = ENV['apiBaseUrl']
const gpiBaseURL = ENV['gpiBaseUrl']
const rsmBaseUrl = ENV['rsmBaseUrl']

const mctRequest = new Request({
  baseURL: apiBaseURL,
  timeout: 60 * 1000,
  interceptors: {
    requestInterceptors: (config) => {
      // console.log('apiBaseURL', apiBaseURL)
      return config
    },
    responseInterceptors: (result) => {
      return result
    }
  }
})

const globalRequest = new Request({
  baseURL: gpiBaseURL,
  timeout: 30 * 1000,
  interceptors: {
    requestInterceptors: (config) => {
      return config
    },
    responseInterceptors: (result) => {
      return result
    }
  }
})

const appiumRequest = new Request({
  baseURL: rsmBaseUrl,
  timeout: 30 * 1000,
  interceptors: {
    requestInterceptors: (config) => {
      return config
    },
    responseInterceptors: (result) => {
      return result
    }
  }
})

export { originalAxios, mctRequest, globalRequest, appiumRequest }
